import i18n from "i18next";
import { I18nService } from "./services/i18nService";
import { getDefaultLanguage } from "./toStdLanguages";

/**
 * Enhanced i18n configuration with remote data loading capabilities.
 */
export class EnhancedI18n {
  private static initialized = false;
  private static remoteDataLoaded = false;

  /**
   * Initializes i18n with both local and remote data.
   *
   * @param {boolean} loadRemoteOnInit - Whether to load remote data on initialization
   * @param {object} remoteFilters - Filters for remote data loading
   */
  public static async initialize(
    loadRemoteOnInit: boolean = true,
    remoteFilters: object = {},
  ): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      // Load remote translations if requested
      if (loadRemoteOnInit) {
        await this.loadRemoteData({
          scope: "GLOBAL", // Default scope, can be adjusted
          language: getDefaultLanguage(), // Use default language
          ...remoteFilters,
        });
      }

      this.initialized = true;
      console.log("Enhanced i18n initialized successfully");
    } catch (error) {
      console.warn(
        "Failed to load remote translations on init, continuing with local data:",
        error,
      );
      this.initialized = true;
    }
  }

  /**
   * Loads remote translation data and merges with existing resources.
   *
   * @param {object} filters - Filters for remote data loading
   * @param {boolean} override - Whether to override existing translations
   */
  public static async loadRemoteData(
    filters: object = {},
    override: boolean = false,
  ): Promise<void> {
    try {
      await I18nService.loadRemoteTranslations(filters, override);
      this.remoteDataLoaded = true;
      console.log("Remote i18n data loaded successfully");
    } catch (error) {
      console.error("Failed to load remote i18n data:", error);
      throw error;
    }
  }

  /**
   * Refreshes all translation data (both local and remote).
   *
   * @param {object} remoteFilters - Filters for remote data loading
   */
  public static async refresh(remoteFilters: object = {}): Promise<void> {
    try {
      await I18nService.refreshTranslations(remoteFilters);
      console.log("i18n data refreshed successfully");
    } catch (error) {
      console.error("Failed to refresh i18n data:", error);
      throw error;
    }
  }

  /**
   * Gets the current initialization status.
   */
  public static getStatus(): {
    initialized: boolean;
    remoteDataLoaded: boolean;
  } {
    return {
      initialized: this.initialized,
      remoteDataLoaded: this.remoteDataLoaded,
    };
  }

  /**
   * Changes the language and optionally loads remote data for the new language.
   *
   * @param {string} language - The target language
   * @param {boolean} loadRemoteData - Whether to load remote data for the new language
   * @param {object} remoteFilters - Additional filters for remote data loading
   */
  public static async changeLanguage(
    language: string,
    loadRemoteData: boolean = true,
    remoteFilters: object = {},
  ): Promise<void> {
    try {
      // Load remote data for the new language if requested
      if (loadRemoteData) {
        const filters = {
          scope: "GLOBAL", // Default scope, can be adjusted
          language,
          ...remoteFilters,
        };
        await this.loadRemoteData(filters);
      }

      console.log(`Language changed to ${language}`);
    } catch (error) {
      console.error(`Failed to change language to ${language}:`, error);
      throw error;
    } finally {
      await i18n.changeLanguage(language);
      localStorage.setItem("lang", language);
      document.documentElement.lang = language;
    }
  }
}

// // Auto-initialize with remote data loading
// EnhancedI18n.initialize().catch((error) => {
//   console.warn("Failed to auto-initialize enhanced i18n:", error);
// });

export default i18n;
